import AxiosInstance from "@/api/axios";
import {
  defaultPagination,
  PaginationInterface
} from "@/components/Base/Pagination";
import { GeneralFilterType, LeadsDataType } from "@/utils/types";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

interface enquiriesFilterType extends GeneralFilterType {
  search?: string | null;
  sort?: string | null;
  order?: string | null;
  currentPage?: number;
  perpage?: number;
  pageLimit: string;
}

let abortController: AbortController | null = null;

export const fetchEnquiries = createAsyncThunk(
  "fetchEnquiries/fetchData",
  async (
    {
      organization_id,
      agent,
      branch_id,
      region_id,
      date_range,
      search,
      currentPage,
      perpage,
      pageLimit,
      sort,
      order,
      reengaged
    }: enquiriesFilterType,
    thunkAPI
  ) => {
    if (abortController) {
      abortController.abort();
    }

    abortController = new AbortController();
    try {
      const response = await AxiosInstance.get(`/leads/enquiry`, {
        params: {
          page: currentPage,
          perpage,
          organization_id,
          branch_id,
          region_id,
          date_range,
          pageLimit,
          agent,
          search,
          sort,
          order,
          reengaged
        },
        signal: abortController.signal
      });
      if (response.data.success) {
        return response.data.data;
      } else {
        return null;
      }
    } catch (error: any) {
      if (!abortController.signal.aborted || error.code === "ERR_CANCELED") {
        return null;
      }
      return thunkAPI.rejectWithValue(error);
    } finally {
      abortController = null;
    }
  }
);

export const deleteEnquiry = createAsyncThunk(
  "enquiries/deleteEnquiry",
  async (
    {
      leadId,
      branch,
      senders,
      region_id
    }: {
      leadId: string;
      branch?: string | null;
      senders?: string[];
      region_id?: string | null;
    },
    thunkAPI
  ) => {
    try {
      const params = new URLSearchParams();
      if (branch) params.append("branch", branch);
      if (region_id) params.append("region_id", region_id);
      if (senders && senders.length)
        params.append("senders", JSON.stringify(senders));

      const response = await AxiosInstance.delete(
        `/leads/${leadId}?${params.toString()}`
      );

      return response.data.success
        ? leadId
        : thunkAPI.rejectWithValue("Failed to delete lead");
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || "An error occurred");
    }
  }
);

interface EnquiriesStateType {
  enquiries: {
    data: LeadsDataType[] | null | undefined;
    all_senders: string[][];
    pagination: PaginationInterface;
    timezone: string;
    showLeadFromColumn: boolean;
  };
  loading: boolean;
  error: string | null;
}

const initialState: EnquiriesStateType = {
  enquiries: {
    all_senders: [[]],
    data: null,
    pagination: defaultPagination,
    timezone: "",
    showLeadFromColumn: false
  },
  loading: false,
  error: null
};

const enquiriesReducer = createSlice({
  name: "enquiries",
  initialState,
  reducers: {
    updatePagination: (state, action) => {
      if (state.enquiries) {
        state.enquiries.pagination = {
          ...state.enquiries?.pagination,
          ...action.payload
        };
      }
    },
    resetPagination: (state, _) => {
      state.enquiries.pagination = defaultPagination;
    },
    updateEnquiryOnAutomationSwitch: (state, action) => {
      const { sender, switchValue } = action.payload;
      if (state.enquiries.data) {
        const leadIndex = state.enquiries.data.findIndex(
          (lead) => lead.sender === sender
        );
        if (leadIndex !== -1) {
          state.enquiries.data[leadIndex].use_automation = switchValue;
        }
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEnquiries.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEnquiries.fulfilled, (state, action) => {
        state.loading = false;
        state.enquiries = action.payload;
      })
      .addCase(fetchEnquiries.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteEnquiry.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteEnquiry.fulfilled, (state, action) => {
        state.loading = false;
        if (state.enquiries.data) {
          state.enquiries.data = state.enquiries.data.filter(
            (lead) => lead._id !== action.payload
          );
        }
      })
      .addCase(deleteEnquiry.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  }
});

export const {
  updatePagination,
  resetPagination,
  updateEnquiryOnAutomationSwitch
} = enquiriesReducer.actions;

export default enquiriesReducer.reducer;
